/**
 * product-price-pp.js
 *
 * Fake Prices logic for product detail pages (PDP).
 *
 * How it works:
 * 1. On page load, reads the base price from #first-price .fake-compare-price .money (rendered by Liquid).
 * 2. Reads discount configuration from the hidden .placeholder-for-cmc block.
 * 3. Calculates the discounted price and writes it into the appropriate #final-price-* element.
 * 4. On variant change, the new theme triggers SSR refresh of the Price block via
 *    theme.pubsub (variantChange event). We subscribe to this event and re-apply
 *    discounts after the DOM has been updated with fresh Liquid output.
 * 5. Formats JS-updated amounts through theme.Currency.formatMoney so Shopify
 *    Markets / active presentment currency display stays consistent.
 *
 * Loaded only when settings.show-fake-compare-price == true AND request.page_type == 'product'.
 */

function parseMoneyPP(text) {
  return parseFloat((text || '').replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
}

function calcDiscountedPP(base, amount, type) {
  if (type === 'percentage') return Math.round(base * (1 - amount / 100));
  return Math.round(base - amount);
}

function formatFakeMoneyPP(amount) {
  return theme.Currency.formatMoneyText(Math.round(amount * 100), theme.settings.moneyFormat);
}

function applyFakePricesOnPDP() {
  // Base price is always correct because Liquid renders the selected variant price.
  // After SSR variant update the entire price block is replaced before this function runs.
  const firstPriceMoneyEl = document.querySelector('#first-price .fake-compare-price .money');
  if (!firstPriceMoneyEl) return;

  const basePrice = parseMoneyPP(firstPriceMoneyEl.textContent);
  if (!basePrice) return;

  // Read discount values from the hidden .placeholder-for-cmc block.
  const get = id => parseFloat(document.querySelector('#' + id)?.textContent || 0);

  const discounts = {
    'final-price':                    { amount: get('discount-amount'),        type: 'amount' },
    'final-price-two':                { amount: get('discount-amount-two'),     type: 'amount' },
    'final-price-precents-desktop':   { amount: get('discount-percents'),       type: 'percentage' },
    'final-price-precents-desktop-two':    { amount: get('discount-percents-two'),    type: 'percentage' },
    'final-price-precents-desktop-three':  { amount: get('discount-percents-three'),  type: 'percentage' },
    'final-price-precents-desktop-fourth': { amount: get('discount-percents-fourth'), type: 'percentage' },
    'final-price-precents-desktop-fifth':  { amount: get('discount-percents-fifth'),  type: 'percentage' },
    'final-price-precents-desktop-sixth':  { amount: get('discount-percents-sixth'),  type: 'percentage' },
  };

  Object.entries(discounts).forEach(([id, cfg]) => {
    if (!cfg.amount) return;
    document.querySelectorAll('#' + id + ' .money').forEach(el => {
      el.textContent = formatFakeMoneyPP(calcDiscountedPP(basePrice, cfg.amount, cfg.type));
    });
  });
}

// Initial application on page load.
applyFakePricesOnPDP();

// Re-apply after SSR variant change.
// The new theme's product-info component publishes variantChange AFTER
// updateSourceFromDestination has already swapped the Price block innerHTML,
// so the DOM is up-to-date when our callback fires.
(function subscribeToVariantChange() {
  if (window.theme && window.theme.pubsub) {
    theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.variantChange, function () {
      applyFakePricesOnPDP();
    });
  } else {
    // theme.js loads with defer; retry until it is ready.
    setTimeout(subscribeToVariantChange, 100);
  }
})();
